import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

import { scrubFilterMenuPortalAriaHidden } from './use_filter_menu_portal_focus_allow'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const PREFERRED_FOCUS_SELECTORS = [
  'input[name="filter-term"]',
  '[data-component="filter-dropdown-menu"] input:not([disabled])',
  '[data-component="filter-dropdown-menu"] button:not([disabled])',
  FOCUSABLE_SELECTOR,
]

const MAX_FOCUS_ATTEMPTS = 12

const focusElement = (element) => {
  if (!(element instanceof HTMLElement)) return false

  element.focus({ preventScroll: true })
  return document.activeElement === element
}

export const focusPortaledFilterFieldMenu = (menuRoot) => {
  if (!(menuRoot instanceof HTMLElement)) return false

  scrubFilterMenuPortalAriaHidden(menuRoot)

  return PREFERRED_FOCUS_SELECTORS.some((selector) => {
    const candidate = menuRoot.querySelector(selector)
    return focusElement(candidate)
  })
}

const observePortaledFilterFieldMenuFocus = (menuRoot) => {
  if (!(menuRoot instanceof HTMLElement)) return undefined

  let cancelled = false
  let observer
  let rafId

  const tryFocus = () => {
    if (cancelled) return true
    return focusPortaledFilterFieldMenu(menuRoot)
  }

  const cleanup = () => {
    cancelled = true
    observer?.disconnect()
    if (rafId != null) {
      cancelAnimationFrame(rafId)
    }
  }

  if (tryFocus()) {
    return cleanup
  }

  observer = new MutationObserver(() => {
    if (tryFocus()) {
      cleanup()
    }
  })

  observer.observe(menuRoot, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden', 'inert', 'disabled'],
  })

  const scheduleAttempt = (attempt = 0) => {
    if (cancelled || tryFocus() || attempt >= MAX_FOCUS_ATTEMPTS) {
      return
    }

    rafId = requestAnimationFrame(() => {
      scheduleAttempt(attempt + 1)
    })
  }

  scheduleAttempt()

  return cleanup
}

/**
 * Portaled filter field menus render outside the filter panel Dialog, so keyboard
 * navigation cannot reach them until focus is moved explicitly on open.
 */
export const useFocusPortaledFilterFieldMenuOnOpen = (isOpen, menuRef) => {
  const cleanupRef = useRef(undefined)

  const stopObserving = useCallback(() => {
    cleanupRef.current?.()
    cleanupRef.current = undefined
  }, [])

  const startObserving = useCallback((menuRoot) => {
    stopObserving()
    cleanupRef.current = observePortaledFilterFieldMenuFocus(menuRoot)
  }, [stopObserving])

  useLayoutEffect(() => {
    if (!isOpen) {
      stopObserving()
      return undefined
    }

    startObserving(menuRef.current)

    return stopObserving
  }, [isOpen, menuRef, startObserving, stopObserving])

  useEffect(() => {
    if (!isOpen) return undefined

    startObserving(menuRef.current)

    return stopObserving
  }, [isOpen, menuRef, startObserving, stopObserving])
}

export const usePortaledFilterFieldMenuFocusRef = (isOpen, menuRef) => {
  const stopObservingRef = useRef(undefined)

  return useCallback((node) => {
    menuRef.current = node
    stopObservingRef.current?.()
    stopObservingRef.current = undefined

    if (node && isOpen) {
      stopObservingRef.current = observePortaledFilterFieldMenuFocus(node)
    }
  }, [isOpen, menuRef])
}
